import i18n from 'i18n-literally'
import { createSelector } from 'redux-bundler'

import type { PermissionState } from '@capacitor/core'
import { captureException } from '@sentry/capacitor'

import { isNativePlatform } from '~/src/Native/platform'
import { disableNativePush, getNativeDeviceAgent, getPushNavigation, initializeNativePush } from '~/src/Native/push'
import { createAppIsReadySelector } from '~/src/Store/utils'

const START = 'NATIVE_PUSH_START'
const PERMISSION = 'NATIVE_PUSH_PERMISSION'
const REGISTERED = 'NATIVE_PUSH_REGISTERED'
const FAILED = 'NATIVE_PUSH_FAILED'
const RESET = 'NATIVE_PUSH_RESET'

type NativePushStatus = 'denied' | 'failed' | 'idle' | 'registered' | 'starting'

interface NativePushState {
  readonly attemptedFacility: number | null
  readonly error: string | null
  readonly permission: PermissionState | null
  readonly registeredFacility: number | null
  readonly status: NativePushStatus
}

type NativePushAction =
  | { payload: number; type: typeof START }
  | { payload: PermissionState; type: typeof PERMISSION }
  | { payload: number; type: typeof REGISTERED }
  | { payload: string; type: typeof FAILED }
  | { type: typeof RESET }

interface NativePushStore {
  doAddSnackbarMessage: (message: string, status: 'error') => void
  doDeviceListSetStateFilter: (filter: 'offline') => void
  doNativePushRegisterToken: (token: string) => Promise<number>
  doUpdateUrl: (url: string) => void
  selectCurrentFacilityId: () => number
}

interface BundleContext {
  apiFetch: (path: string, body: unknown, options: { emptyResponse: true; method: 'POST' }) => Promise<unknown>
  dispatch: (action: NativePushAction) => void
  store: NativePushStore
}

const initialState: Readonly<NativePushState> = {
  attemptedFacility: null,
  error: null,
  permission: null,
  registeredFacility: null,
  status: 'idle',
}

const reportNativePushFailure = (
  error: unknown,
  stage: 'initialization' | 'listener',
  dispatch: BundleContext['dispatch'],
  store: NativePushStore,
): void => {
  if (!(error instanceof Error)) throw new TypeError('Native push returned a non-Error failure.')
  captureException(error, { tags: { native_push_stage: stage } })
  dispatch({ type: FAILED, payload: error.message })
  store.doAddSnackbarMessage(i18n`Push notifications could not be enabled. You can continue using AROYA.`, 'error')
}

export const getNativePushLogoutReaction = (
  { authenticated }: { authenticated: boolean },
  { attemptedFacility }: NativePushState,
  native = isNativePlatform(),
): { actionCreator: 'doNativePushDisable' } | null => {
  if (!native || authenticated || attemptedFacility === null) return null
  return { actionCreator: 'doNativePushDisable' }
}

export default {
  name: 'nativePush',
  reducer: (state: Readonly<NativePushState> = initialState, action: NativePushAction): Readonly<NativePushState> => {
    switch (action.type) {
      case START:
        return { ...state, attemptedFacility: action.payload, error: null, status: 'starting' }
      case PERMISSION:
        return { ...state, permission: action.payload, status: action.payload === 'granted' ? 'starting' : 'denied' }
      case REGISTERED:
        return { ...state, registeredFacility: action.payload, status: 'registered' }
      case FAILED:
        return { ...state, error: action.payload, status: 'failed' }
      case RESET:
        return initialState
      default:
        return state
    }
  },
  selectNativePush: (state: { nativePush: NativePushState }): NativePushState => state.nativePush,
  doNativePushRegisterToken:
    (token: string, getDeviceAgent: typeof getNativeDeviceAgent = getNativeDeviceAgent) =>
    async ({ apiFetch, store }: BundleContext): Promise<number> => {
      const facilityId = store.selectCurrentFacilityId()
      const agent = await getDeviceAgent()
      await apiFetch('/notifications/device/', { token, agent }, { method: 'POST', emptyResponse: true })
      return facilityId
    },
  doNativePushDisable:
    (disablePush: typeof disableNativePush = disableNativePush) =>
    async ({ dispatch }: Pick<BundleContext, 'dispatch'>): Promise<void> => {
      await disablePush()
      dispatch({ type: RESET })
    },
  doNativePushInitialize:
    (facilityId: number, initializePush: typeof initializeNativePush = initializeNativePush) =>
    async ({ dispatch, store }: Pick<BundleContext, 'dispatch' | 'store'>): Promise<void> => {
      dispatch({ type: START, payload: facilityId })
      try {
        const registerToken = async (token: string): Promise<number> => {
          const registeredFacility = await store.doNativePushRegisterToken(token)
          dispatch({ type: REGISTERED, payload: registeredFacility })
          return registeredFacility
        }
        const navigate = (rawData: Record<string, unknown>): void => {
          const { url, deviceStateFilter } = getPushNavigation(rawData, store.selectCurrentFacilityId())
          if (deviceStateFilter) store.doDeviceListSetStateFilter(deviceStateFilter)
          store.doUpdateUrl(url)
        }
        const result = await initializePush({
          onNotificationAction: navigate,
          onError: error => reportNativePushFailure(error, 'listener', dispatch, store),
          onToken: registerToken,
        })
        dispatch({ type: PERMISSION, payload: result.permission })
        if (!result.token) return
        await registerToken(result.token)
      } catch (error) {
        reportNativePushFailure(error, 'initialization', dispatch, store)
      }
    },
  reactNativePush: createAppIsReadySelector({
    defaultReturnValue: null,
    dependencies: ['selectNativePush', 'selectCurrentFacilityId'],
    resultFn: ({ attemptedFacility }: NativePushState, facilityId: number) => {
      if (!isNativePlatform() || attemptedFacility === facilityId) return null
      return { actionCreator: 'doNativePushInitialize', args: [facilityId] }
    },
  }),
  reactNativePushLogout: createSelector('selectAuth', 'selectNativePush', getNativePushLogoutReaction),
}
